/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { isPrd } from "constants/domain"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import { sleep } from "utils/misc"
import { TroyWebTracker } from "utils/tracking"
import { MEMORY_COLLECTION_INTERVAL_SEC } from "./constant"

interface MemoryMonitorConfig {
  // if interval is not a positive number, then the monitor will be turned off
  memoryCollectionIntervalSec: number
}

export class MemoryMonitor {
  private initializedOn: Date

  private interval: ReturnType<typeof setInterval> | null = null

  private config: Partial<MemoryMonitorConfig> = {}

  private runCount: number = 0

  private getAppOpenDuration(): number {
    return Date.now() - this.initializedOn.valueOf()
  }

  /**
   * collects and sends metrics to Scribe
   */
  private async collectMemoryMetrics() {
    if (!isPrd()) {
      // not really essential - just thought it'd be useful for Grabbers to figure out if their memory is being collected
      // eslint-disable-next-line no-console
      console.debug("Collecting memory metrics.")
    }
    const { memoryCollectionIntervalSec } = this.config
    const appOpenDuration = this.getAppOpenDuration()
    const runCount = this.runCount
    this.runCount += 1

    // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measureUserAgentSpecificMemory
    if (
      window.crossOriginIsolated &&
      performance.measureUserAgentSpecificMemory
    ) {
      try {
        const results = await performance.measureUserAgentSpecificMemory()
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.performance,
          action: EventActionEng.submitMemoryMetrics,
          labels: {
            totalMemory: results.bytes,
            fullResult: JSON.stringify(results),
            memoryCollectionIntervalSec,
            appOpenDuration,
            runCount,
          },
        })
      } catch (e) {
        // note: headless Chrome sometimes fails - not that important if it fails so we just record it to Scribe
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.performance,
          action: EventActionEng.submitMemoryMetricsFail,
          labels: {
            memoryCollectionIntervalSec,
            appOpenDuration,
            runCount,
            errorMsg: e instanceof Error ? e.message : JSON.stringify(e),
            stack: e instanceof Error ? e.stack : undefined,
          },
        })
      }
    }
  }

  start() {
    if (this.isRunning()) {
      return
    }
    const { memoryCollectionIntervalSec } = this.config
    if (memoryCollectionIntervalSec && memoryCollectionIntervalSec >= 1) {
      this.collectMemoryMetrics()
      this.interval = setInterval(
        this.collectMemoryMetrics.bind(this),
        memoryCollectionIntervalSec * 1000,
      )
    }
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  isRunning() {
    return !!this.interval
  }

  setConfig(cfg: Partial<MemoryMonitorConfig>): MemoryMonitor {
    const wasRunning = this.isRunning()
    this.stop()
    this.config = {
      ...this.config,
      ...cfg,
    }
    if (wasRunning) {
      this.start()
    }
    return this
  }

  async initialize() {
    if (this.initializedOn) {
      // already initialized - just start it
      this.start()
      return
    }
    this.initializedOn = new Date()
    this.setConfig({
      memoryCollectionIntervalSec: MEMORY_COLLECTION_INTERVAL_SEC,
    })
    // wait for 10 seconds for memory to stabilize before we collect metrics
    await sleep(10_000)
    this.start()
  }
}
